import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Mail, Instagram, Facebook, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { PHONE, PHONE_LINK, TAGLINE } from "@/lib/brand";

export default function Contact() {
  return (
    <div data-testid="contact-page" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-20">
      <SEO
        title="Contact"
        description="Contact The Velvet Brush — call 8318512668 for bookings and details. Lucknow | Available for travel."
        path="/contact"
      />
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">{TAGLINE}</p>
        <h1 className="mt-3 font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">Let's talk about your moment</h1>
        <p className="mt-4 text-ink/65 leading-relaxed">
          The fastest way to reach us is a call or a quick enquiry — we respond to every message personally.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        <motion.a
          href={PHONE_LINK}
          data-testid="contact-call-card"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl border border-[#EAE5DF] p-8 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300 block"
        >
          <Phone className="w-7 h-7 text-velvet" />
          <h2 className="mt-4 font-serif text-2xl text-ink">Call Us</h2>
          <p className="mt-2 text-2xl font-semibold text-velvet">{PHONE}</p>
          <p className="mt-2 text-sm text-ink/55">Tap to call — we're happy to discuss availability, looks and pricing.</p>
        </motion.a>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-2xl border border-[#EAE5DF] p-8" data-testid="contact-location-card"
        >
          <MapPin className="w-7 h-7 text-velvet" />
          <h2 className="mt-4 font-serif text-2xl text-ink">Studio</h2>
          <p className="mt-2 text-lg font-semibold text-ink">Lucknow</p>
          <p className="mt-2 text-sm text-ink/55">Available for travel across India — destination weddings welcome.</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-2xl border border-[#EAE5DF] p-8" data-testid="contact-social-card"
        >
          <Mail className="w-7 h-7 text-velvet" />
          <h2 className="mt-4 font-serif text-2xl text-ink">Follow Along</h2>
          <p className="mt-2 text-sm text-ink/55">Latest looks, behind-the-scenes and real brides.</p>
          <div className="mt-4 flex gap-3">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="contact-instagram"
              className="w-11 h-11 rounded-full bg-cream flex items-center justify-center text-ink hover:bg-blush transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" data-testid="contact-facebook"
              className="w-11 h-11 rounded-full bg-cream flex items-center justify-center text-ink hover:bg-blush transition-colors">
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-10 bg-ink text-ivory rounded-[2rem] p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
        data-testid="contact-cta-band"
      >
        <div>
          <h2 className="font-serif text-2xl sm:text-3xl">Prefer to write it down?</h2>
          <p className="mt-2 text-ivory/60 text-sm">Submit the enquiry form — no account needed — and we'll call you back.</p>
        </div>
        <Link to="/enquire">
          <Button data-testid="contact-enquire-btn" className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold px-8 h-12 active:scale-95 transition-transform">
            Enquire Now <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </motion.div>
    </div>
  );
}
